import { useMemo } from "react";

import { cn } from "@/lib/utils";

export type GenerativeContourVariant = "cover" | "logo";

export interface GenerativeContourProps {
  /** Organization name — the only seed. The same name always renders the same field. */
  name: string;
  /**
   * "cover" — a wide contour field with no monogram (sits behind a cover surface).
   * "logo"  — a square field with a centered monogram letter (the logo-tile fallback).
   */
  variant?: GenerativeContourVariant;
  /**
   * Explicit ring count. Defaults to a hash-derived spacing that fills the box.
   * Pass a smaller count (e.g. 8) for condensed thumbnails so the contour reads
   * cleanly at tiny sizes.
   */
  rings?: number;
  /**
   * Override the monogram glyph(s) for `variant="logo"`. Defaults to the first
   * alphanumeric character of `name`. Accepts 1–2 characters.
   */
  monogram?: string;
  className?: string;
}

/**
 * Branded generative fallback — a deterministic contour field keyed to the org
 * name, matching the imagery guidelines (design system refresh §04).
 *
 * The product never shows random stock or a broken image: when no real cover or
 * logo is supplied, this renders instead. It is tonal and monochrome by design —
 * a lighter tone of one dark ground, reading like a contour map rather than
 * decoration. Because the field is seeded purely from the name, a brokerage's
 * identity stays stable everywhere it appears.
 *
 * Grounds and lines are pulled from the design tokens (src/index.css), never
 * hardcoded hex.
 */

/** Tonal dark grounds — one hue family, monochrome by design. */
const GROUNDS = [
  "hsl(var(--teal-primary))",
  "hsl(var(--teal-deep))",
  "hsl(var(--ink-900))",
] as const;

/** FNV-1a — small, stable, dependency-free string hash → unsigned 32-bit int. */
const hashName = (name: string): number => {
  let h = 0x811c9dc5;
  const seed = (name || "").trim().toLowerCase();
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

const firstGlyph = (name: string): string => {
  const match = (name || "").trim().match(/[A-Za-z0-9]/);
  return match ? match[0].toUpperCase() : "•";
};

const GenerativeContour = ({
  name,
  variant = "cover",
  rings: ringCount,
  monogram,
  className,
}: GenerativeContourProps) => {
  const field = useMemo(() => {
    const hash = hashName(name);
    // Off-centre origin and ring spacing, both derived from the name hash so the
    // pattern is deterministic but distinct per organization.
    const ground = GROUNDS[hash % GROUNDS.length];
    const cx = 18 + ((hash >> 3) % 64); // 18–82 (% of the 100×100 viewBox)
    const cy = 18 + ((hash >> 9) % 64);
    const gap = 7 + ((hash >> 15) % 7); // 7–13 ring spacing

    // Concentric rings out far enough to cover the box from any origin. When an
    // explicit `rings` count is given (condensed thumbs), emit exactly that many.
    const rings: number[] = [];
    if (ringCount && ringCount > 0) {
      for (let i = 1; i <= ringCount; i++) rings.push(i * gap);
    } else {
      for (let r = gap; r <= 170; r += gap) rings.push(r);
    }

    return { ground, cx, cy, rings };
  }, [name, ringCount]);

  const glyph = (monogram ?? firstGlyph(name)).slice(0, 2);

  return (
    <div
      className={cn("relative h-full w-full overflow-hidden", className)}
      style={{ backgroundColor: field.ground }}
      aria-hidden="true"
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        fill="none"
      >
        {field.rings.map((r, i) => (
          <circle
            key={i}
            cx={field.cx}
            cy={field.cy}
            r={r}
            stroke="#ffffff"
            // Lighter tone of the single ground — monochrome contour lines.
            strokeOpacity={0.1}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>

      {variant === "logo" && (
        // Monogram drawn in viewBox units so it scales with the tile from a 30px
        // compare-tray thumb up to a 150px cover — undistorted (meet), centred.
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
        >
          <text
            x="50"
            y="50"
            textAnchor="middle"
            dominantBaseline="central"
            className="font-display"
            fontWeight={600}
            fontSize={glyph.length > 1 ? 34 : 44}
            fill="#ffffff"
            fillOpacity={0.9}
          >
            {glyph}
          </text>
        </svg>
      )}
    </div>
  );
};

export default GenerativeContour;
